/**
 * ByoWebhookConfigurator.jsx — Single-stop config form for BYO-Webhook campaigns.
 *
 * Composes:
 *   - URL + auth picker (None | Custom Header)
 *   - "Saved Presets" dropdown (load + save)
 *   - <VariableMappingForm /> for {{n}} → JSON key mapping
 *   - "Test Webhook" button → calls audienceSource.api.testWebhook
 *   - Reserved-keys checklist (lives inside VariableMappingForm)
 *   - Hard-send-cap input (max messages)
 *   - "Generate Developer Contract" → opens <DeveloperContractModal />
 *
 * Props:
 *   templateId:        string | number   (currently picked template)
 *   templateVariables: string[]          e.g. ['1','2','3']
 *   value:             { url, auth, hard_send_cap, saved_preset_id, mapping }
 *   onChange:          (next) => void
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Input,
  Select,
  Button,
  InputNumber,
  Alert,
  Space,
  Tag,
  Modal,
  message,
  Tooltip,
  Spin,
  Checkbox,
} from 'antd';
import {
  ExperimentOutlined,
  FileTextOutlined,
  SaveOutlined,
  ApiOutlined,
  ReloadOutlined,
  WarningOutlined,
  CopyOutlined,
  CheckOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import VariableMappingForm from './VariableMappingForm';
import DeveloperContractModal from './DeveloperContractModal';
import audienceSourceApi from '../../api/audienceSource.api';
import './ByoWebhookConfigurator.css';

const AUTH_TYPES = [
  { value: 'none', label: 'No Auth' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'header', label: 'Custom Header' },
];

const DEFAULT_VALUE = {
  url: '',
  auth: { type: 'none' },
  hard_send_cap: 10000,
  saved_preset_id: null,
  mapping: {},
};

export default function ByoWebhookConfigurator({
  templateId,
  templateVariables = [],
  value = DEFAULT_VALUE,
  onChange,
}) {
  const v = { ...DEFAULT_VALUE, ...value };

  const [presets, setPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [contractOpen, setContractOpen] = useState(false);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Signing secret reveal (shown once after create or rotate)
  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [secretConfirmed, setSecretConfirmed] = useState(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const showSecretModal = (secret) => {
    setRevealedSecret(secret);
    setSecretConfirmed(false);
    setCopiedSecret(false);
    setSecretModalOpen(true);
  };

  const update = useCallback(
    (patch) => onChange?.({ ...v, ...patch }),
    [v, onChange]
  );

  const updateAuth = (patch) =>
    update({ auth: { ...v.auth, ...patch } });

  /* ── load presets ── */
  const loadPresets = useCallback(async () => {
    try {
      setLoadingPresets(true);
      const res = await audienceSourceApi.listPresets();
      // Handle both { success, data: [] } and legacy { entity: { presets: [] } } envelopes
      const body = res.data?.data || res.data?.entity || res.data;
      const list = body?.presets || body?.items || (Array.isArray(body) ? body : []);
      setPresets(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load presets:', err);
    } finally {
      setLoadingPresets(false);
    }
  }, []);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  const applyPreset = (presetId) => {
    const p = presets.find((x) => (x.preset_id ?? x.id) === presetId);
    if (!p) {
      update({ saved_preset_id: null });
      return;
    }
    update({
      saved_preset_id: p.preset_id ?? p.id,
      url: p.url,
      auth: p.auth || { type: 'none' },
      hard_send_cap: p.hard_send_cap || 10000,
      mapping: p.default_mapping || p.field_mapping || {},
    });
    message.success(`Loaded preset "${p.name}"`);
  };

  /* ── test webhook ── */
  const runTest = async () => {
    if (!v.url) {
      message.error('Please enter a webhook URL first');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const testPayload = v.saved_preset_id
        ? { url: v.url, preset_id: v.saved_preset_id, templateId, templatemapping: v.mapping, hard_send_cap: v.hard_send_cap }
        : { url: v.url, auth: { type: v.auth?.type || 'none', header_name: v.auth?.header_name }, templateId, templatemapping: v.mapping, hard_send_cap: v.hard_send_cap };
      const res = await audienceSourceApi.testWebhook(testPayload);
      // Handle { success, data: { http_status, latency_ms, data_count, ... } } envelope
      const data = res.data?.data || res.data?.entity || res.data;
      const normalized = {
        ok: data?.ok ?? (data?.http_status >= 200 && data?.http_status < 300) ?? false,
        status_code: data?.status_code || data?.http_status,
        latency_ms: data?.latency_ms,
        columns_detected: data?.columns_detected || Object.keys((data?.sample_body_preview?.data?.[0] || data?.sample?.[0] || {})),
        total_count_returned: data?.total_count_returned ?? data?.data_count ?? data?.record_count ?? null,
        next_cursor_returned: data?.next_cursor_returned,
        error: data?.error,
        missing_required: data?.missing_required,
        errors_per_row: data?.errors_per_row,
      };
      setTestResult(normalized);
      if (normalized.ok) {
        message.success(`Webhook responded — ${normalized.total_count_returned != null ? normalized.total_count_returned + ' contacts' : 'OK'}`);
      } else {
        message.warning('Webhook responded but had issues — review below');
      }
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      let msg;
      if (status === 404) {
        msg = `Your server returned 404 — the endpoint "${v.url}" does not exist yet. Ask your developer to build and deploy it first, then test again.`;
      } else if (status === 401 || status === 403) {
        msg = `Authentication failed (${status}) — check the Auth setting above and ensure your endpoint is configured to accept the signed request.`;
      } else if (status === 500) {
        msg = `Your server returned a 500 Internal Server Error. Check your server logs for the error.`;
      } else if (!status) {
        msg = `Could not reach "${v.url}". Make sure the server is running, the URL is public (not localhost), and allows HTTPS connections.`;
      } else {
        msg = serverMsg || `HTTP ${status} — ${err.message}`;
      }
      setTestResult({ ok: false, error: msg });
      message.error('Webhook test failed — see details below');
    } finally {
      setTesting(false);
    }
  };

  /* ── save as preset ── */
  const savePreset = async () => {
    if (!presetName.trim()) {
      message.error('Please name the preset');
      return;
    }
    try {
      const res = await audienceSourceApi.createPreset({
        name: presetName.trim(),
        url: v.url,
        auth: {
          type: v.auth?.type || 'none',
          ...(v.auth?.type === 'bearer' && { token: v.auth.token }),
          ...(v.auth?.type === 'header' && { header_name: v.auth.header_name, token: v.auth.token }),
        },
        hard_send_cap: v.hard_send_cap,
        default_mapping: v.mapping,
      });
      // Handle { success, data: { preset_id, signing_secret } } envelope
      const entity = res.data?.data || res.data?.entity || res.data;
      const signingSecret = entity?.signing_secret || res.data?.signing_secret;
      setSavePresetOpen(false);
      setPresetName('');
      loadPresets();
      if (signingSecret) {
        showSecretModal(signingSecret);
      } else {
        message.success('Preset saved');
      }
    } catch (err) {
      message.error(`Failed to save: ${err?.response?.data?.message || err.message}`);
    }
  };

  /* ── rotate signing secret ── */
  const rotateSecret = () => {
    Modal.confirm({
      title: 'Rotate Signing Secret?',
      content: (
        <div>
          <p>This will generate a new signing secret and <strong>immediately invalidate the current one</strong>.</p>
          <p style={{ color: '#ff4d4f' }}>Make sure to update your webhook endpoint before rotating, or verification will break.</p>
        </div>
      ),
      okText: 'Rotate Secret',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await audienceSourceApi.rotatePresetSecret(v.saved_preset_id);
          // Handle { success, data: { signing_secret } } envelope
          const entity = res.data?.data || res.data?.entity || res.data;
          const signingSecret = entity?.signing_secret || res.data?.signing_secret;
          if (signingSecret) {
            showSecretModal(signingSecret);
          } else {
            message.success('Signing secret rotated');
          }
        } catch (err) {
          message.error(`Failed to rotate: ${err?.response?.data?.message || err.message}`);
        }
      },
    });
  };

  const detectedColumns = testResult?.columns_detected || [];
  const loadedPreset = v.saved_preset_id ? presets.find((p) => (p.preset_id ?? p.id) === v.saved_preset_id) : null;

  return (
    <div className="byo-config">
      {/* Saved presets */}
      <div className="byo-row">
        <label className="byo-label">Saved Presets</label>
        <Space.Compact style={{ width: '100%' }}>
          <Select
            style={{ flex: 1 }}
            placeholder="Choose a saved preset (or configure manually below)"
            value={v.saved_preset_id || undefined}
            onChange={applyPreset}
            allowClear
            loading={loadingPresets}
            options={presets.map((p) => ({ value: p.preset_id ?? p.id, label: p.name }))}
            notFoundContent={loadingPresets ? <Spin size="small" /> : 'No presets saved yet'}
          />
          <Tooltip title="Reload presets">
            <Button icon={<ReloadOutlined />} onClick={loadPresets} />
          </Tooltip>
          {!v.saved_preset_id && (
            <Button icon={<SaveOutlined />} onClick={() => setSavePresetOpen(true)}>
              Save Current
            </Button>
          )}
        </Space.Compact>
        {loadedPreset && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <Tag color="green" style={{ margin: 0 }}>✓ Preset loaded — URL &amp; auth configured</Tag>
            <Button icon={<SyncOutlined />} danger size="small" onClick={rotateSecret}>
              Rotate Secret
            </Button>
          </div>
        )}
      </div>

      {/* URL — editable only when no preset loaded */}
      {loadedPreset ? (
        <div className="byo-row">
          <label className="byo-label"><ApiOutlined /> Webhook URL</label>
          <Input value={v.url} readOnly style={{ background: '#f5f5f5', cursor: 'default', color: '#555' }}
            addonBefore={<Tag style={{ margin: 0 }} color="purple">{v.auth?.type || 'none'}</Tag>}
          />
          <small className="byo-hint">URL and auth credentials are stored in the preset and sent securely at campaign launch.</small>
        </div>
      ) : (
        <>
          {/* URL */}
          <div className="byo-row">
            <label className="byo-label"><ApiOutlined /> Webhook URL</label>
            <Input
              placeholder="https://your-server.com/api/audiences/diwali"
              value={v.url}
              onChange={(e) => update({ url: e.target.value })}
              size="large"
            />
            <small className="byo-hint">
              MessageOS sends a <strong>POST</strong> to this URL at campaign launch time. Your endpoint must return{' '}
              <code>{'{"success":true,"count":N,"data":[{"mobile":"91...","full_name":"..."}]}'}</code>.
              The <code>mobile</code> field (with country code, no <code>+</code>) is required per record. &nbsp;
              <strong>Must be a publicly reachable HTTPS URL</strong> — not localhost.
            </small>
          </div>

          {/* Auth */}
          <div className="byo-row">
            <label className="byo-label">Authentication</label>
            <Select
              style={{ width: 220 }}
              value={v.auth?.type || 'none'}
              onChange={(t) => updateAuth({ type: t, header_name: v.auth?.header_name, token: undefined })}
              options={AUTH_TYPES}
            />
            {v.auth?.type === 'bearer' && (
              <Input.Password
                placeholder="Bearer token (e.g. eyJhbGci...)"
                value={v.auth.token || ''}
                onChange={(e) => updateAuth({ token: e.target.value })}
                style={{ marginTop: 8 }}
              />
            )}
            {v.auth?.type === 'header' && (
              <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                <Input
                  placeholder="Header name (e.g. X-Audience-Secret)"
                  value={v.auth.header_name || ''}
                  onChange={(e) => updateAuth({ header_name: e.target.value })}
                />
                <Input.Password
                  placeholder="Token / secret value"
                  value={v.auth.token || ''}
                  onChange={(e) => updateAuth({ token: e.target.value })}
                />
              </Space.Compact>
            )}
            {v.auth?.type !== 'none' && (
              <small className="byo-hint" style={{ marginTop: 4 }}>
                Token is stored encrypted. MessageOS also sends an <code>X-MOS-Signature</code> HMAC header so your endpoint can verify every request.
              </small>
            )}
          </div>
        </>
      )}

      {/* Hard cap */}
      <div className="byo-row">
        <label className="byo-label">Hard Send Cap</label>
        <Space>
          <InputNumber
            min={1}
            max={1000000}
            step={1000}
            value={v.hard_send_cap}
            onChange={(n) => update({ hard_send_cap: n || 10000 })}
            style={{ width: 160 }}
          />
          <Tag color="gold">Stops at this count even if your webhook returns more</Tag>
        </Space>
      </div>

      {/* Test webhook — run this FIRST so columns populate the mapping below */}
      <div className="byo-actions" style={{ marginBottom: 0 }}>
        <Button
          type="primary"
          icon={<ExperimentOutlined />}
          loading={testing}
          onClick={runTest}
          disabled={!v.url}
        >
          Test Webhook
        </Button>
        <Button
          icon={<FileTextOutlined />}
          onClick={() => setContractOpen(true)}
          disabled={!templateId}
        >
          Generate Developer Contract
        </Button>
      </div>

      {/* Test result */}
      {testResult && (
        <div className="byo-test-result">
          {testResult.ok ? (
            <Alert
              type="success"
              showIcon
              message="Webhook reachable — select column mappings below"
              description={
                <div>
                  <div style={{ marginBottom: 4 }}>HTTP {testResult.status_code} · {testResult.latency_ms}ms · {testResult.total_count_returned != null ? `${testResult.total_count_returned} contacts in sample` : ''}</div>
                  <div>
                    Detected keys:{' '}
                    {(testResult.columns_detected || []).map((c) => (
                      <Tag key={c} color="blue">{c}</Tag>
                    ))}
                  </div>
                </div>
              }
            />
          ) : (
            <Alert
              type="error"
              showIcon
              message="Webhook test failed — fix before sending"
              description={
                <div>
                  {testResult.error && <div style={{ marginBottom: 8 }}>{testResult.error}</div>}
                  {testResult.missing_required?.length > 0 && (
                    <div>Missing required keys: <strong>{testResult.missing_required.join(', ')}</strong></div>
                  )}
                  <div style={{ marginTop: 8, color: '#888' }}>
                    You can still click <strong>Next</strong> to proceed without a successful test,
                    but the campaign will fail at send time if the endpoint is not working.
                  </div>
                </div>
              }
            />
          )}
        </div>
      )}

      {/* Variable mapping — shown after test so detected columns populate dropdowns */}
      <div className="byo-row">
        <VariableMappingForm
          templateVariables={templateVariables}
          detectedColumns={detectedColumns}
          value={v.mapping}
          onChange={(mapping) => update({ mapping })}
        />
      </div>
      <Modal
        open={savePresetOpen}
        title="Save Webhook as Preset"
        onCancel={() => setSavePresetOpen(false)}
        onOk={savePreset}
        okText="Save"
      >
        <Input
          placeholder="Preset name (e.g. Diwali Buyers)"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
        />
      </Modal>

      {/* Developer contract */}
      <DeveloperContractModal
        open={contractOpen}
        onClose={() => setContractOpen(false)}
        templateId={templateId}
        templatemapping={v.mapping}
      />

      {/* Signing Secret Reveal Modal — shown once after create or rotate */}
      <Modal
        open={secretModalOpen}
        title={null}
        footer={null}
        width={480}
        closable={false}
        maskClosable={false}
        destroyOnClose
      >
        <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
          <WarningOutlined style={{ fontSize: 32, color: '#d48806', display: 'block', marginBottom: 8 }} />
          <h3 style={{ margin: '8px 0 4px', fontSize: 16 }}>Share With Your Webhook Provider</h3>
          <p style={{ color: '#555', marginBottom: 0, fontSize: 13, lineHeight: 1.6 }}>
            This secret is <strong>not for you</strong> — send it to the team running the webhook
            server. They must save it as an environment variable so their endpoint can verify
            that requests genuinely come from MessageOS.
          </p>
        </div>
        <div style={{ background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '12px 16px', fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all', marginBottom: 8 }}>
          {revealedSecret}
        </div>
        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 12, lineHeight: 1.7 }}>
          <strong>Tell your webhook provider to:</strong><br />
          1. Store it: <code>MOS_SIGNING_SECRET={revealedSecret}</code><br />
          2. On every incoming POST, verify the <code>X-MOS-Signature</code> header using this secret.<br />
          3. See the <strong>Developer Guide</strong> in API Docs → BYO Campaigns for full verification code.
        </div>
        <Button
          type="primary"
          icon={copiedSecret ? <CheckOutlined /> : <CopyOutlined />}
          block
          size="large"
          style={{ marginBottom: 16, background: copiedSecret ? '#52c41a' : undefined, borderColor: copiedSecret ? '#52c41a' : undefined }}
          onClick={async () => {
            await copyToClipboard(revealedSecret);
            setCopiedSecret(true);
            message.success('Signing secret copied');
            setTimeout(() => setCopiedSecret(false), 3000);
          }}
        >
          {copiedSecret ? 'Copied!' : 'Copy Signing Secret'}
        </Button>
        <div style={{ padding: '12px 16px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, marginBottom: 16 }}>
          <Checkbox checked={secretConfirmed} onChange={(e) => setSecretConfirmed(e.target.checked)}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>I've shared this secret with my webhook provider</span>
          </Checkbox>
        </div>
        <Button
          type="primary"
          ghost
          block
          disabled={!secretConfirmed}
          onClick={() => { setSecretModalOpen(false); setRevealedSecret(null); setCopiedSecret(false); setSecretConfirmed(false); }}
        >
          Done
        </Button>
      </Modal>
    </div>
  );
}
